import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./rekapNegatifPoin.css";
import SearchableSelect from "./SearchableSelect";

// SIP_HRD_REKAP_NEGATIF_POIN_STAGING_V1_5

const API = "/api/rekap-negatif-poin";

const EMPTY_FORM = {
  jobprofil_id: "",
  nik: "",
  nama: "",
  departemen: "",
  jabatan: "",
  periode_awal: "",
  periode_akhir: "",
  jumlah_hadir: 0,
  tidak_hadir_m: 0,
  tidak_hadir_i: 0,
  tidak_hadir_s: 0,
  tidak_hadir_ipa: 0,
  sanksi_sp: 0,
  cuti: 0,
  catatan: "",
};

const DEFAULT_SETTINGS = {
  bobot_m: 50,
  bobot_i: 7.5,
  bobot_s: 5,
  bobot_ipa: 2.5,
  bobot_sp: 10,
  maks_np_baik: 35,
};

const asNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const fmt = (v) =>
  Number(v || 0).toLocaleString("id-ID", {
    minimumFractionDigits: Number(v || 0) % 1 ? 1 : 0,
    maximumFractionDigits: 2,
  });

const fmtDate = (v) => {
  if (!v) return "-";
  const s = String(v).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : s;
};

const dateToYMD = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const excelSerialToDate = (serial) => {
  const utcDays = Math.floor(Number(serial) - 25569);
  const utcValue = utcDays * 86400;
  const dateInfo = new Date(utcValue * 1000);
  return new Date(
    dateInfo.getUTCFullYear(),
    dateInfo.getUTCMonth(),
    dateInfo.getUTCDate()
  );
};

const parsePeriod = (value) => {
  const s = String(value || "").trim();
  const m = s.match(
    /From Date\s+(\d{2})-(\d{2})-(\d{4})\s+To\s+(\d{2})-(\d{2})-(\d{4})/i
  );

  if (!m) return { periode_awal: "", periode_akhir: "" };

  return {
    periode_awal: `${m[3]}-${m[2]}-${m[1]}`,
    periode_akhir: `${m[6]}-${m[5]}-${m[4]}`,
  };
};

const calcPreview = (form, settings) => {
  const np_m = asNum(form.tidak_hadir_m) * asNum(settings.bobot_m);
  const np_i = asNum(form.tidak_hadir_i) * asNum(settings.bobot_i);
  const np_s = asNum(form.tidak_hadir_s) * asNum(settings.bobot_s);
  const np_ipa = asNum(form.tidak_hadir_ipa) * asNum(settings.bobot_ipa);
  const np_sp = asNum(form.sanksi_sp) * asNum(settings.bobot_sp);
  const total = np_m + np_i + np_s + np_ipa + np_sp;

  return {
    np_m,
    np_i,
    np_s,
    np_ipa,
    np_sp,
    total,
    status: total <= asNum(settings.maks_np_baik) ? "BAIK" : "BURUK",
  };
};

export default function RekapNegatifPoin() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsForm, setSettingsForm] = useState(DEFAULT_SETTINGS);
  const [employees, setEmployees] = useState([]);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [deleted, setDeleted] = useState("0");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selected, setSelected] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);

  const preview = useMemo(
    () => calcPreview(form, settings),
    [form, settings]
  );

  const params = (overrides = {}) => ({
    q,
    status,
    from,
    to,
    deleted,
    page,
    limit,
    ...overrides,
  });

  const loadData = async (overrides = {}) => {
    setLoading(true);
    try {
      const p = params(overrides);
      const [listRes, summaryRes] = await Promise.all([
        axios.get(API, { params: p }),
        axios.get(`${API}/summary`, { params: p }),
      ]);

      setRows(Array.isArray(listRes.data?.rows) ? listRes.data.rows : []);
      setPagination(
        listRes.data?.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 1,
        }
      );
      setSummary(summaryRes.data || {});
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Gagal mengambil Rekap Negatif Poin.");
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      const s = res.data || DEFAULT_SETTINGS;
      setSettings(s);
      setSettingsForm(s);
    } catch (err) {
      console.error(err);
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await axios.get(`${API}/employees`);
      setEmployees(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadSettings();
    loadEmployees();
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, deleted]);

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      jobprofil_id: row.jobprofil_id || "",
      nik: row.nik || "",
      nama: row.nama || "",
      departemen: row.departemen || "",
      jabatan: row.jabatan || "",
      periode_awal: row.periode_awal
        ? String(row.periode_awal).slice(0, 10)
        : "",
      periode_akhir: row.periode_akhir
        ? String(row.periode_akhir).slice(0, 10)
        : "",
      jumlah_hadir: row.jumlah_hadir || 0,
      tidak_hadir_m: row.tidak_hadir_m || 0,
      tidak_hadir_i: row.tidak_hadir_i || 0,
      tidak_hadir_s: row.tidak_hadir_s || 0,
      tidak_hadir_ipa: row.tidak_hadir_ipa || 0,
      sanksi_sp: row.sanksi_sp || 0,
      cuti: row.cuti || 0,
      catatan: row.catatan || "",
    });
    setShowForm(true);
  };

  const chooseEmployee = (id) => {
    const emp = employees.find((x) => String(x.id) === String(id));
    if (!emp) {
      setForm((f) => ({ ...f, jobprofil_id: "" }));
      return;
    }

    setForm((f) => ({
      ...f,
      jobprofil_id: emp.id,
      nik: emp.nik || "",
      nama: emp.nama || "",
      departemen: emp.line_dept || "",
      jabatan: emp.jabatan || "",
    }));
  };

  const saveForm = async (e) => {
    e.preventDefault();

    if (!form.jobprofil_id) {
      alert("Karyawan wajib dipilih dari Job Profile.");
      return;
    }

    if (!String(form.nik || "").trim() || !String(form.nama || "").trim()) {
      alert("Data karyawan pada Job Profile belum lengkap.");
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API}/${editingId}`, form);
      } else {
        await axios.post(API, form);
      }

      setShowForm(false);
      resetForm();
      setPage(1);
      await loadData({ page: 1 });
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal menyimpan data.");
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Hapus data ${row.nama} ke Sampah?`)) return;

    try {
      await axios.delete(`${API}/${row.id}`);
      await loadData();
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal menghapus data.");
    }
  };

  const restoreRow = async (row) => {
    if (!window.confirm(`Restore data ${row.nama}?`)) return;

    try {
      await axios.post(`${API}/${row.id}/restore`);
      await loadData();
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal restore data.");
    }
  };

  const openDetail = (row) => {
    setSelected(row);
    setShowDetail(true);
  };

  const openHistory = async (row) => {
    try {
      const res = await axios.get(`${API}/history`, {
        params: { record_id: row.id },
      });
      setSelected(row);
      setHistoryRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setShowHistory(true);
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal mengambil history.");
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();

    if (
      !window.confirm(
        "Simpan master bobot dan hitung ulang seluruh Rekap Negatif Poin?"
      )
    ) {
      return;
    }

    try {
      const res = await axios.put(`${API}/settings`, settingsForm);
      setSettings(res.data?.settings || settingsForm);
      setShowSettings(false);
      await loadData();
      alert(res.data?.message || "Master bobot berhasil disimpan.");
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal menyimpan master bobot.");
    }
  };

  const recalculate = async () => {
    if (!window.confirm("Hitung ulang semua data menggunakan Master Bobot saat ini?"))
      return;

    try {
      const res = await axios.post(`${API}/recalculate`);
      await loadData();
      alert(res.data?.message || "Recalculate selesai.");
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal menghitung ulang.");
    }
  };

  const exportExcel = async () => {
    try {
      const res = await axios.get(API, {
        params: {
          q,
          status,
          from,
          to,
          deleted,
          page: 1,
          limit: 50000,
        },
      });

      const exportRows = Array.isArray(res.data?.rows) ? res.data.rows : [];

      if (!exportRows.length) {
        alert("Tidak ada data untuk diexport.");
        return;
      }

      const mapped = exportRows.map((r, idx) => ({
        NO: idx + 1,
        ID: r.nik || "",
        "FULL NAME": r.nama || "",
        "JOB POSITION": r.jabatan || "",
        DEPARTEMEN: r.departemen || "",
        "PERIODE AWAL": r.periode_awal
          ? String(r.periode_awal).slice(0, 10)
          : "",
        "PERIODE AKHIR": r.periode_akhir
          ? String(r.periode_akhir).slice(0, 10)
          : "",
        H: r.jumlah_hadir || 0,
        M: r.tidak_hadir_m || 0,
        I: r.tidak_hadir_i || 0,
        S: r.tidak_hadir_s || 0,
        IPA: r.tidak_hadir_ipa || 0,
        SP: r.sanksi_sp || 0,
        CUTI: r.cuti || 0,
        "NP M": Number(r.np_m || 0),
        "NP I": Number(r.np_i || 0),
        "NP S": Number(r.np_s || 0),
        "NP IPA": Number(r.np_ipa || 0),
        "NP SP": Number(r.np_sp || 0),
        "TOTAL NP": Number(r.np_total || 0),
        STATUS: r.status_np || "",
        CATATAN: r.catatan || "",
      }));

      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Rekap Negatif Poin");

      const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `rekap_negatif_poin_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (err) {
      alert("Gagal export Excel.");
    }
  };

  const downloadTemplate = () => {
    const template = [
      [
        "No.",
        "ID",
        "Full Name",
        "Job Position",
        "Penarikan Data Karyawan (PKWT/PKWTT)",
        "H",
        "M",
        "I",
        "S",
        "IPA",
        "SP",
        "CUTI",
      ],
      [
        1,
        "25010131",
        "CONTOH KARYAWAN",
        "STAFF",
        "From Date 01-01-2026 To 31-12-2026",
        200,
        0,
        1,
        0,
        0,
        0,
        5,
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");

    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "template_rekap_negatif_poin.xlsx"
    );
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array", cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(ws, {
        header: 1,
        defval: null,
        raw: true,
      });

      let headerIndex = -1;

      for (let i = 0; i < Math.min(matrix.length, 15); i += 1) {
        const row = matrix[i] || [];
        const text = row.map((x) => String(x || "").trim().toLowerCase());
        if (
          text.includes("id") &&
          text.some((x) => x.includes("full name")) &&
          text.some((x) => x.includes("job position"))
        ) {
          headerIndex = i;
          break;
        }
      }

      // Format file asli mempunyai 2 baris header; data dimulai setelah sub-header H/M/I/S...
      let start = headerIndex >= 0 ? headerIndex + 1 : 5;

      if (
        matrix[start] &&
        String(matrix[start]?.[5] || "").trim().toUpperCase() === "H"
      ) {
        start += 1;
      }

      const imported = [];

      for (let i = start; i < matrix.length; i += 1) {
        const r = matrix[i] || [];
        const nik = String(r[1] ?? "").trim();
        const nama = String(r[2] ?? "").trim();

        if (!nik || !nama) continue;

        const period = parsePeriod(r[4]);

        imported.push({
          nik,
          nama,
          jabatan: String(r[3] ?? "").trim(),
          periode_awal: period.periode_awal,
          periode_akhir: period.periode_akhir,
          jumlah_hadir: asNum(r[5]),
          tidak_hadir_m: asNum(r[6]),
          tidak_hadir_i: asNum(r[7]),
          tidak_hadir_s: asNum(r[8]),
          tidak_hadir_ipa: asNum(r[9]),
          sanksi_sp: asNum(r[10]),
          cuti: asNum(r[11]),
          sumber: file.name,
        });
      }

      if (!imported.length) {
        alert("Tidak ada data valid yang terbaca dari Excel.");
        return;
      }

      if (
        !window.confirm(
          `Import ${imported.length} baris? Data karyawan akan dicocokkan berdasarkan NIK ke Job Profile. NIK yang belum ada di Job Profile akan di-skip. Data dengan NIK+periode yang sama akan diupdate.`
        )
      ) {
        return;
      }

      const res = await axios.post(`${API}/import`, { rows: imported });
      await loadData({ page: 1 });
      setPage(1);

      alert(
        `${res.data?.message || "Import selesai"}\n` +
          `Baru: ${res.data?.inserted || 0}\n` +
          `Update: ${res.data?.updated || 0}\n` +
          `Skip: ${res.data?.skipped || 0}`
      );
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Gagal import Excel.");
    }
  };

  const applySearch = () => {
    setPage(1);
    loadData({ page: 1 });
  };

  const resetFilter = () => {
    setQ("");
    setStatus("");
    setFrom("");
    setTo("");
    setDeleted("0");
    setPage(1);
    setTimeout(() => {
      loadData({
        q: "",
        status: "",
        from: "",
        to: "",
        deleted: "0",
        page: 1,
      });
    }, 0);
  };

  const renderHistoryData = (value) => {
    if (!value) return "-";
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return String(value);
    }
  };

  return (
    <div className="np-page">
      <div className="np-header">
        <div>
          <h2>Rekap Negatif Poin</h2>
          <div className="np-subtitle">
            HRD • Monitoring Attendance & Negative Point Karyawan
          </div>
        </div>

        <div className="np-toolbar">
          <button className="np-btn np-btn-primary" onClick={openCreate}>
            + Tambah Data
          </button>

          <label className="np-btn np-btn-outline np-file-label">
            Import Excel
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImport}
              hidden
            />
          </label>

          <button className="np-btn np-btn-outline" onClick={downloadTemplate}>
            Template
          </button>

          <button className="np-btn np-btn-outline" onClick={exportExcel}>
            Export
          </button>

          <button
            className="np-btn np-btn-dark"
            onClick={() => {
              setSettingsForm(settings);
              setShowSettings(true);
            }}
          >
            Master Bobot
          </button>

          <button className="np-btn np-btn-warning" onClick={recalculate}>
            Hitung Ulang
          </button>
        </div>
      </div>

      <div className="np-summary-grid">
        <div className="np-card">
          <div className="np-card-label">Total Data</div>
          <div className="np-card-value">{fmt(summary.total)}</div>
        </div>
        <div className="np-card np-card-good">
          <div className="np-card-label">Status BAIK</div>
          <div className="np-card-value">{fmt(summary.baik)}</div>
        </div>
        <div className="np-card np-card-bad">
          <div className="np-card-label">Status BURUK</div>
          <div className="np-card-value">{fmt(summary.buruk)}</div>
        </div>
        <div className="np-card">
          <div className="np-card-label">Total NP</div>
          <div className="np-card-value">{fmt(summary.total_np)}</div>
        </div>
        <div className="np-card">
          <div className="np-card-label">Rata-rata NP</div>
          <div className="np-card-value">{fmt(summary.avg_np)}</div>
        </div>
        <div className="np-card">
          <div className="np-card-label">NP Tertinggi</div>
          <div className="np-card-value">{fmt(summary.max_np)}</div>
        </div>
      </div>

      <div className="np-filter-card">
        <div className="np-filter-grid">
          <div>
            <label>Pencarian</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="NIK, Nama, Jabatan, Departemen..."
            />
          </div>

          <div>
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Semua Status</option>
              <option value="BAIK">BAIK</option>
              <option value="BURUK">BURUK</option>
            </select>
          </div>

          <div>
            <label>Periode Dari</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>

          <div>
            <label>Periode Sampai</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>

          <div>
            <label>Data</label>
            <select
              value={deleted}
              onChange={(e) => {
                setDeleted(e.target.value);
                setPage(1);
              }}
            >
              <option value="0">Aktif</option>
              <option value="1">Sampah</option>
              <option value="all">Semua</option>
            </select>
          </div>

          <div>
            <label>Rows</label>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={50000}>ALL</option>
            </select>
          </div>
        </div>

        <div className="np-filter-actions">
          <button className="np-btn np-btn-primary" onClick={applySearch}>
            Cari
          </button>
          <button className="np-btn np-btn-light" onClick={resetFilter}>
            Reset
          </button>
        </div>
      </div>

      <div className="np-master-info">
        <strong>Bobot:</strong>
        <span>M = {fmt(settings.bobot_m)}</span>
        <span>I = {fmt(settings.bobot_i)}</span>
        <span>S = {fmt(settings.bobot_s)}</span>
        <span>IPA = {fmt(settings.bobot_ipa)}</span>
        <span>SP = {fmt(settings.bobot_sp)}</span>
        <span>Maks NP BAIK = {fmt(settings.maks_np_baik)}</span>
      </div>

      <div className="np-table-card">
        <div className="np-table-title">Data Rekap Negatif Poin Karyawan</div>

        <div className="np-table-wrap">
          <table className="np-table">
            <thead>
              <tr>
                <th rowSpan="2">No</th>
                <th rowSpan="2">NIK</th>
                <th rowSpan="2">Nama</th>
                <th rowSpan="2">Jabatan</th>
                <th rowSpan="2">Periode</th>
                <th colSpan="7">Attendance</th>
                <th colSpan="5">Negatif Point</th>
                <th rowSpan="2">Total NP</th>
                <th rowSpan="2">Status</th>
                <th rowSpan="2">Aksi</th>
              </tr>
              <tr>
                <th>H</th>
                <th>M</th>
                <th>I</th>
                <th>S</th>
                <th>IPA</th>
                <th>SP</th>
                <th>Cuti</th>
                <th>M</th>
                <th>I</th>
                <th>S</th>
                <th>IPA</th>
                <th>SP</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="20" className="np-empty">
                    Memuat data...
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((row, idx) => (
                  <tr key={row.id}>
                    <td>
                      {(pagination.page - 1) * pagination.limit + idx + 1}
                    </td>
                    <td className="np-nowrap">{row.nik}</td>
                    <td className="np-name">{row.nama}</td>
                    <td>{row.jabatan || "-"}</td>
                    <td className="np-nowrap">
                      {fmtDate(row.periode_awal)}
                      <br />
                      s/d {fmtDate(row.periode_akhir)}
                    </td>

                    <td>{fmt(row.jumlah_hadir)}</td>
                    <td>{fmt(row.tidak_hadir_m)}</td>
                    <td>{fmt(row.tidak_hadir_i)}</td>
                    <td>{fmt(row.tidak_hadir_s)}</td>
                    <td>{fmt(row.tidak_hadir_ipa)}</td>
                    <td>{fmt(row.sanksi_sp)}</td>
                    <td>{fmt(row.cuti)}</td>

                    <td>{fmt(row.np_m)}</td>
                    <td>{fmt(row.np_i)}</td>
                    <td>{fmt(row.np_s)}</td>
                    <td>{fmt(row.np_ipa)}</td>
                    <td>{fmt(row.np_sp)}</td>

                    <td className="np-total">{fmt(row.np_total)}</td>
                    <td>
                      <span
                        className={`np-status ${
                          row.status_np === "BURUK"
                            ? "np-status-bad"
                            : "np-status-good"
                        }`}
                      >
                        {row.status_np}
                      </span>
                    </td>

                    <td>
                      <div className="np-actions">
                        <button onClick={() => openDetail(row)}>Detail</button>
                        <button onClick={() => openHistory(row)}>History</button>

                        {Number(row.is_deleted) === 1 ? (
                          <button
                            className="np-action-restore"
                            onClick={() => restoreRow(row)}
                          >
                            Restore
                          </button>
                        ) : (
                          <>
                            <button
                              className="np-action-edit"
                              onClick={() => openEdit(row)}
                            >
                              Edit
                            </button>
                            <button
                              className="np-action-delete"
                              onClick={() => deleteRow(row)}
                            >
                              Hapus
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="20" className="np-empty">
                    Belum ada data Rekap Negatif Poin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="np-pagination">
          <div>
            Total {Number(pagination.total || 0).toLocaleString("id-ID")} data
            • Halaman {pagination.page} dari {pagination.totalPages}
          </div>

          <div>
            <button
              disabled={pagination.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <button
              disabled={pagination.page >= pagination.totalPages}
              onClick={() =>
                setPage((p) => Math.min(pagination.totalPages, p + 1))
              }
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showForm && (
        <div className="np-modal-backdrop">
          <div className="np-modal np-modal-lg">
            <div className="np-modal-header">
              <div>
                <h3>{editingId ? "Edit" : "Tambah"} Rekap Negatif Poin</h3>
                <small>Nilai NP dihitung otomatis dari Master Bobot.</small>
              </div>
              <button
                className="np-close"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={saveForm}>
              <div className="np-form-grid">
                <div className="np-span-2">
                  <label>Karyawan dari Job Profile *</label>
                  <SearchableSelect
                    value={form.jobprofil_id}
                    onChange={(e) => chooseEmployee(e.target.value)}
                    required
                  >
                    <option value="">-- Pilih Karyawan dari Job Profile --</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.nik} - {emp.nama} - {emp.line_dept || "-"}
                      </option>
                    ))}
                  </SearchableSelect>
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#64748b",
                    }}
                  >
                    NIK, Nama, Departemen, dan Jabatan otomatis mengikuti data Job Profile.
                  </div>
                </div>

                <div>
                  <label>NIK *</label>
                  <input
                    value={form.nik}
                    readOnly
                    required
                    title="Data otomatis dari Job Profile"
                  />
                </div>

                <div>
                  <label>Nama *</label>
                  <input
                    value={form.nama}
                    readOnly
                    required
                    title="Data otomatis dari Job Profile"
                  />
                </div>

                <div>
                  <label>Departemen</label>
                  <input
                    value={form.departemen}
                    readOnly
                    title="Data otomatis dari Job Profile"
                  />
                </div>

                <div>
                  <label>Jabatan</label>
                  <input
                    value={form.jabatan}
                    readOnly
                    title="Data otomatis dari Job Profile"
                  />
                </div>

                <div>
                  <label>Periode Awal</label>
                  <input
                    type="date"
                    value={form.periode_awal}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, periode_awal: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <label>Periode Akhir</label>
                  <input
                    type="date"
                    value={form.periode_akhir}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, periode_akhir: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="np-section-title">Jumlah Kehadiran / Ketidakhadiran</div>

              <div className="np-number-grid">
                {[
                  ["jumlah_hadir", "Hadir (H)"],
                  ["tidak_hadir_m", "M"],
                  ["tidak_hadir_i", "I"],
                  ["tidak_hadir_s", "S"],
                  ["tidak_hadir_ipa", "IPA"],
                  ["sanksi_sp", "Sanksi SP"],
                  ["cuti", "Cuti"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label>{label}</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={form[key]}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          [key]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>

              <div className="np-preview">
                <div>
                  NP M
                  <strong>{fmt(preview.np_m)}</strong>
                </div>
                <div>
                  NP I
                  <strong>{fmt(preview.np_i)}</strong>
                </div>
                <div>
                  NP S
                  <strong>{fmt(preview.np_s)}</strong>
                </div>
                <div>
                  NP IPA
                  <strong>{fmt(preview.np_ipa)}</strong>
                </div>
                <div>
                  NP SP
                  <strong>{fmt(preview.np_sp)}</strong>
                </div>
                <div className="np-preview-total">
                  TOTAL NP
                  <strong>{fmt(preview.total)}</strong>
                </div>
                <div>
                  STATUS
                  <strong
                    className={
                      preview.status === "BURUK"
                        ? "np-text-bad"
                        : "np-text-good"
                    }
                  >
                    {preview.status}
                  </strong>
                </div>
              </div>

              <div className="np-form-single">
                <label>Catatan</label>
                <textarea
                  rows="3"
                  value={form.catatan}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, catatan: e.target.value }))
                  }
                />
              </div>

              <div className="np-modal-actions">
                <button
                  type="button"
                  className="np-btn np-btn-light"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                >
                  Batal
                </button>
                <button type="submit" className="np-btn np-btn-primary">
                  Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="np-modal-backdrop">
          <div className="np-modal">
            <div className="np-modal-header">
              <div>
                <h3>Master Bobot Negatif Poin</h3>
                <small>
                  Perubahan bobot akan menghitung ulang seluruh data.
                </small>
              </div>
              <button className="np-close" onClick={() => setShowSettings(false)}>
                ×
              </button>
            </div>

            <form onSubmit={saveSettings}>
              <div className="np-settings-grid">
                {[
                  ["bobot_m", "Bobot M"],
                  ["bobot_i", "Bobot I"],
                  ["bobot_s", "Bobot S"],
                  ["bobot_ipa", "Bobot IPA"],
                  ["bobot_sp", "Bobot SP"],
                  ["maks_np_baik", "Maksimal NP Status BAIK"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label>{label}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={settingsForm[key]}
                      onChange={(e) =>
                        setSettingsForm((f) => ({
                          ...f,
                          [key]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>

              <div className="np-modal-actions">
                <button
                  type="button"
                  className="np-btn np-btn-light"
                  onClick={() => setShowSettings(false)}
                >
                  Batal
                </button>
                <button type="submit" className="np-btn np-btn-primary">
                  Simpan & Hitung Ulang
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetail && selected && (
        <div className="np-modal-backdrop">
          <div className="np-modal np-modal-lg">
            <div className="np-modal-header">
              <h3>Detail Rekap Negatif Poin</h3>
              <button className="np-close" onClick={() => setShowDetail(false)}>
                ×
              </button>
            </div>

            <div className="np-detail-grid">
              {[
                ["NIK", selected.nik],
                ["Nama", selected.nama],
                ["Departemen", selected.departemen],
                ["Jabatan", selected.jabatan],
                ["Periode Awal", fmtDate(selected.periode_awal)],
                ["Periode Akhir", fmtDate(selected.periode_akhir)],
                ["Hadir", selected.jumlah_hadir],
                ["M", selected.tidak_hadir_m],
                ["I", selected.tidak_hadir_i],
                ["S", selected.tidak_hadir_s],
                ["IPA", selected.tidak_hadir_ipa],
                ["SP", selected.sanksi_sp],
                ["Cuti", selected.cuti],
                ["NP M", fmt(selected.np_m)],
                ["NP I", fmt(selected.np_i)],
                ["NP S", fmt(selected.np_s)],
                ["NP IPA", fmt(selected.np_ipa)],
                ["NP SP", fmt(selected.np_sp)],
                ["Total NP", fmt(selected.np_total)],
                ["Status", selected.status_np],
                ["Sumber", selected.sumber],
                ["Catatan", selected.catatan || "-"],
              ].map(([label, value]) => (
                <div className="np-detail-item" key={label}>
                  <span>{label}</span>
                  <strong>{value ?? "-"}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showHistory && selected && (
        <div className="np-modal-backdrop">
          <div className="np-modal np-modal-xl">
            <div className="np-modal-header">
              <div>
                <h3>History Perubahan</h3>
                <small>
                  {selected.nik} - {selected.nama}
                </small>
              </div>
              <button className="np-close" onClick={() => setShowHistory(false)}>
                ×
              </button>
            </div>

            <div className="np-history-list">
              {historyRows.length ? (
                historyRows.map((h) => (
                  <div className="np-history-card" key={h.id}>
                    <div className="np-history-head">
                      <strong>{h.aksi}</strong>
                      <span>{String(h.created_at || "").replace("T", " ").slice(0, 19)}</span>
                    </div>

                    <div className="np-history-columns">
                      <div>
                        <b>Sebelum</b>
                        <pre>{renderHistoryData(h.data_sebelum)}</pre>
                      </div>
                      <div>
                        <b>Sesudah</b>
                        <pre>{renderHistoryData(h.data_sesudah)}</pre>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="np-empty">Belum ada history perubahan.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
